import { NextRequest, NextResponse } from 'next/server'
import { eq } from 'drizzle-orm'
import { db } from '@/db'
import { auditLog, tenantConfigs } from '@/db/schema'
import { requireAdminApiUser } from '@/lib/role-guard'

const VALID_PROVIDERS = new Set(['anthropic'])
const VALID_MODELS = new Set(['claude-haiku-4-5-20251001', 'claude-sonnet-4-6', 'claude-opus-4-7'])

type ConfigPatchBody = {
  submissionPromptText?: unknown
  additionalInfoPromptText?: unknown
  submissionMaxChars?: unknown
  aiProvider?: unknown
  aiModel?: unknown
  aiTemperature?: unknown
  minInfoScoreThreshold?: unknown
  maxClarificationCycles?: unknown
  aiJobTimeoutSeconds?: unknown
  dataProcessingConsent?: unknown
}

export async function PATCH(request: NextRequest) {
  const guard = await requireAdminApiUser()
  if (!guard.ok) return guard.response

  const body = (await request.json().catch(() => null)) as ConfigPatchBody | null
  if (!body || typeof body !== 'object') {
    return NextResponse.json({ error: 'Invalid request body' }, { status: 400 })
  }

  const [existing] = await db
    .select()
    .from(tenantConfigs)
    .where(eq(tenantConfigs.tenantId, guard.user.tenantId))
    .limit(1)

  if (!existing) return NextResponse.json({ error: 'Tenant config not found' }, { status: 404 })

  const updates: Partial<typeof tenantConfigs.$inferInsert> = {}

  if (typeof body.submissionPromptText === 'string' && body.submissionPromptText.trim()) {
    updates.submissionPromptText = body.submissionPromptText.trim()
  }
  if (typeof body.additionalInfoPromptText === 'string' && body.additionalInfoPromptText.trim()) {
    updates.additionalInfoPromptText = body.additionalInfoPromptText.trim()
  }
  if (typeof body.submissionMaxChars === 'number' && body.submissionMaxChars >= 500 && body.submissionMaxChars <= 20000) {
    updates.submissionMaxChars = Math.round(body.submissionMaxChars)
  }
  if (typeof body.aiProvider === 'string' && VALID_PROVIDERS.has(body.aiProvider)) {
    updates.aiProvider = body.aiProvider
  }
  if (typeof body.aiModel === 'string' && VALID_MODELS.has(body.aiModel)) {
    updates.aiModel = body.aiModel
  }
  if (typeof body.aiTemperature === 'number' && body.aiTemperature >= 0 && body.aiTemperature <= 1) {
    updates.aiTemperature = body.aiTemperature
  }
  if (typeof body.minInfoScoreThreshold === 'number' && body.minInfoScoreThreshold >= 0 && body.minInfoScoreThreshold <= 1) {
    updates.minInfoScoreThreshold = body.minInfoScoreThreshold
  }
  if (typeof body.maxClarificationCycles === 'number' && body.maxClarificationCycles >= 0 && body.maxClarificationCycles <= 10) {
    updates.maxClarificationCycles = Math.round(body.maxClarificationCycles)
  }
  if (typeof body.aiJobTimeoutSeconds === 'number' && body.aiJobTimeoutSeconds >= 30 && body.aiJobTimeoutSeconds <= 600) {
    updates.aiJobTimeoutSeconds = Math.round(body.aiJobTimeoutSeconds)
  }
  if (typeof body.dataProcessingConsent === 'boolean') {
    updates.dataProcessingConsent = body.dataProcessingConsent
  }

  if (Object.keys(updates).length === 0) {
    return NextResponse.json({ error: 'No valid fields to update' }, { status: 400 })
  }

  updates.updatedAt = new Date()

  const [updated] = await db
    .update(tenantConfigs)
    .set(updates)
    .where(eq(tenantConfigs.tenantId, guard.user.tenantId))
    .returning()

  await db.insert(auditLog).values({
    tenantId: guard.user.tenantId,
    actorId: guard.user.id,
    actorType: 'user',
    action: 'config.updated',
    entityType: 'tenant_config',
    entityId: existing.id,
    payload: { fields: Object.keys(updates).filter((k) => k !== 'updatedAt') },
  })

  return NextResponse.json({ config: updated })
}
